import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { SystemService, WorkflowContextService } from '../../../../services';
import { 
  ExternalDataConnection, 
  ParcelDataConnection, 
  SearchParameter 
} from '../../../../models';
import { CommonDropdownData } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';

@Component({
  selector: 'wm-settings-data-connections',
  standalone: false,
  templateUrl: './settings-data-connections.component.html',
  styleUrls: ['./settings-data-connections.component.scss'],
  encapsulation: ViewEncapsulation.None 
})
export class SettingsDataConnectionsComponent implements OnInit, OnDestroy {
  isLoading = false; 
  showNewInline = false;
  
  // Data object for the new Common Dropdown mapping the connection types
  connectionTypeDropdownData: CommonDropdownData = {
    placeholder: 'Connection Type',
    options: [
      { label: 'ParcelData', value: 'ParcelData' },
      { label: 'SecureFTP', value: 'SecureFTP' }
    ]
  };

  parcelDataSources: any[] = [];
  connections: ExternalDataConnection[] = [];
  
  newConnection: ExternalDataConnection;
  newConnectionSourceId: string; 

  errorMessage: string | null = null;
  clientSubscription: Subscription;
  routerSub$: Subscription;
  expandedState: boolean[] = [];
  
  activeEditId: string | null = null;

  parcelDataExists = false;

  constructor(
    private _systemSvc: SystemService,
    private _context: WorkflowContextService,
    private _toastr: ToastrService,
    private _cd: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    if (history.state) {
      if (history.state.connections) {
        this.connections = history.state.connections;
        this.expandedState = new Array(this.connections.length).fill(false);
      }
      if (history.state.parcelDataSources) {
        this.parcelDataSources = history.state.parcelDataSources;
      }
      if (history.state.parcelDataExists !== undefined) {
        this.parcelDataExists = history.state.parcelDataExists;
      }
    }

    if (this._context.client) {
      this.initData();
    } else {
      this.clientSubscription = this._context.client$.subscribe(c => {
        if (c) {
          this.initData();
        }
      });
    }

    this.syncUrl();
    this.routerSub$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncUrl();
    });
  }

  ngOnDestroy() {
    if (this.clientSubscription) this.clientSubscription.unsubscribe();
    if (this.routerSub$) this.routerSub$.unsubscribe();
  }

  initData() {
    if (!this.connections || this.connections.length === 0) {
      this.loadConnections();
    }
    
    if (!this.parcelDataSources || this.parcelDataSources.length === 0) {
      this.loadParcelSources();
    }
  }

  syncUrl() {
    const urlStr = this.router.url;
    if (urlStr.includes('/data/add')) {
      this.setupNewEditor();
    } else if (urlStr.includes('/data/edit/')) {
      const id = urlStr.split('/data/edit/')[1].split(/[?#]/)[0];
      this.activeEditId = id;
      this.showNewInline = false;
      this.applyExpand();
    } else {
      this.closeAllEditors();
    }
  }

  loadConnections() {
    this.isLoading = true; 
    this.errorMessage = null;

    this._systemSvc.searchExternalDataConnections({
      searchText: '',
      clientId: this._context.client.id
    }).subscribe({
      next: (res) => {
        this.connections = res || [];
        this.expandedState = new Array(this.connections.length).fill(false);
        this.parcelDataExists = this.connections.some(c => c.connectionType === 'ParcelData');
        this.isLoading = false; 
        this.applyExpand();
      },
      error: (err) => {
        console.error(err);
        this.errorMessage = 'Failed to load connections.';
        this.isLoading = false; 
      }
    });
  }

  loadParcelSources() {
    this._systemSvc.getParcelDataSources().subscribe(
      res => this.parcelDataSources = res || [],
      err => this.parcelDataSources = []
    );
  }

  applyExpand() {
    if (this.activeEditId && this.connections.length > 0) {
      this.expandedState.fill(false);
      const index = this.connections.findIndex(c => c.id === this.activeEditId);
      if (index > -1) {
        this.expandedState[index] = true;
        const connId = this.connections[index].id;
        
        this._systemSvc.getExternalDataConnection(connId).subscribe(fullConn => {
          if (fullConn.connectionType === 'ParcelData') {
             this.hydrateParcelSource(fullConn);
          }
          this.connections[index] = fullConn;
        }, err => {
          this._toastr.error('Failed to load connection details.');
          this.expandedState[index] = false; 
        });
      } else {
         this.navigateToBase();
      }
    }
  }

  navigateToBase() {
    const baseUrl = this.router.url.split('/data')[0] + '/data/list';
    this.router.navigateByUrl(baseUrl, { 
      state: { 
        connections: this.connections, 
        parcelDataSources: this.parcelDataSources,
        parcelDataExists: this.parcelDataExists
      } 
    });
  }

  setupNewEditor() {
    this.showNewInline = true;
    this.activeEditId = null;
    this.expandedState.fill(false);
    this.newConnection = new ExternalDataConnection();
    if (this._context.client) {
      this.newConnection.clientID = this._context.client.id;
    }
    this.newConnectionSourceId = null;
    this.errorMessage = null;
  }

  closeAllEditors() {
    this.showNewInline = false;
    this.activeEditId = null;
    this.expandedState.fill(false);
  }

  toggleAccordion(index: number) {
    const wasOpen = this.expandedState[index];
    
    if (!wasOpen) {
      const conn = this.connections[index];
      const baseUrl = this.router.url.split('/data')[0] + '/data';
      this.router.navigateByUrl(`${baseUrl}/edit/${conn.id}`, { 
        state: { 
          connections: this.connections, 
          parcelDataSources: this.parcelDataSources,
          parcelDataExists: this.parcelDataExists
        } 
      });
    } else {
      this.navigateToBase();
    }
  }

  hydrateParcelSource(conn: ExternalDataConnection) {
    if (!conn.parameters) conn.parameters = [];
    
    const sourceParam = conn.parameters.find(p => p.name && p.name.toLowerCase() === 'sourceid');
    
    (conn as any).tempSourceId = sourceParam ? sourceParam.value : null;
  }

  compareIds(o1: any, o2: any): boolean {
    return (o1 == o2); 
  }

  onEditConnectionTypeChange(conn: ExternalDataConnection, newType: any) {
    conn.connectionType = newType;
    
    if (newType === 'ParcelData' && !conn.name) {
       conn.name = 'Parcel Data Connection';
    }
  }

  deleteConnection(index: number, conn: ExternalDataConnection) {
    if (confirm(`Are you sure you want to delete ${conn.name}?`)) {
      this._systemSvc.deleteExternalDataConection(conn).subscribe(() => {
        this._toastr.success('Connection deleted');
        
        this.connections = this.connections.filter(c => c.id !== conn.id);
        this.parcelDataExists = this.connections.some(c => c.connectionType === 'ParcelData');
        
        if (this.activeEditId === conn.id) {
           this.navigateToBase();
        }
      });
    }
  }

  cancelEdit(index: number) {
    this.navigateToBase();
  }

  saveConnection(index: number, conn: ExternalDataConnection) {
    if (conn.connectionType === 'ParcelData') {
      const otherParcel = this.connections.find((c, i) => i !== index && c.connectionType === 'ParcelData');
      if (otherParcel) {
        this._toastr.error('A Parcel Data Source has already been configured.');
        return;
      }
      
      this.updateParcelParameter(conn, (conn as any).tempSourceId);
      
      if (!conn.name) conn.name = 'Parcel Data Connection';
    } else {
      if (!conn.name) {
        this._toastr.error('Name is required');
        return;
      }
    }

    this._systemSvc.saveExternalDataConnection(conn).subscribe(() => {
      this._systemSvc.searchExternalDataConnections({ searchText: '', clientId: this._context.client.id }).subscribe(res => {
         this.connections = res || [];
         this.parcelDataExists = this.connections.some(c => c.connectionType === 'ParcelData');
         this._toastr.success('Connection saved');
         this.navigateToBase();
      });
    }, err => this._toastr.error('Error saving connection'));
  }

  openNewConnection() {
    const baseUrl = this.router.url.split('/data')[0] + '/data/add';
    // Passing state prevents component from wiping data on route navigation
    this.router.navigateByUrl(baseUrl, { 
      state: { 
        connections: this.connections, 
        parcelDataSources: this.parcelDataSources,
        parcelDataExists: this.parcelDataExists
      } 
    });
  }

  closeNewConnection() {
    this.navigateToBase();
  }

  onNewConnectionTypeChange(type: any) {
    const clientId = this._context.client.id;
    if (type === 'ParcelData') {
      this.newConnection = new ParcelDataConnection();
      this.newConnection.name = 'Parcel Data Connection';
    } else {
      this.newConnection = new ExternalDataConnection();
    }
    this.newConnection.connectionType = type;
    this.newConnection.clientID = clientId;
  }

  saveNewConnection() {
    this.errorMessage = null;
    const nc = this.newConnection;

    if (!nc.connectionType) {
      this.errorMessage = 'Connection Type is required.';
      return;
    }

    if (nc.connectionType === 'ParcelData') {
      if (this.parcelDataExists) {
        this.errorMessage = 'A Parcel Data Source has already been configured.';
        return;
      }
      if (!this.newConnectionSourceId) {
        this.errorMessage = 'Please select a Source.';
        return;
      }
      this.updateParcelParameter(nc, this.newConnectionSourceId);
      if(!nc.name) nc.name = 'Parcel Data Connection'; 
    } else {
      if (!nc.name) {
         this.errorMessage = 'Name is required.';
         return;
      }
    }

    this._systemSvc.saveExternalDataConnection(nc).subscribe(() => {
      this._systemSvc.searchExternalDataConnections({ searchText: '', clientId: this._context.client.id }).subscribe(res => {
         this.connections = res || [];
         this.parcelDataExists = this.connections.some(c => c.connectionType === 'ParcelData');
         this._toastr.success('New connection created');
         this.navigateToBase();
      });
    }, err => {
      this.errorMessage = 'Failed to save connection.';
      console.error(err);
    });
  }

  private updateParcelParameter(conn: ExternalDataConnection, sourceId: string) {
    if (!conn.parameters) conn.parameters = [];
    let sourceParam = conn.parameters.find(p => p.name === 'SourceID');
    if (!sourceParam) {
      sourceParam = new SearchParameter({
        label: 'Source ID',
        name: 'SourceID',
        value: null
      });
      conn.parameters.push(sourceParam);
    }
    sourceParam.value = sourceId;
  }

  testConnection(conn: ExternalDataConnection) {
    if (!conn) return;
    this._systemSvc.testExternalConnection(conn).subscribe(
        result => {
          if (result) this._toastr.success('Connection Successful');
          else this._toastr.error('Connection Failed');
        },
        err => this._toastr.error('Connection Failed')
    );
  }
}